/**
 * Password Generator Component
 * Generates secure passwords that meet all requirements
 */
class PasswordGenerator {
    constructor() {
        this.requirements = {
            minLength: 8,
            maxLength: 128,
            requireUppercase: true,
            requireLowercase: true,
            requireNumbers: true,
            requireSymbols: true,
            excludeSimilar: true,
            excludeAmbiguous: true
        };

        this.characterSets = {
            lowercase: 'abcdefghijklmnopqrstuvwxyz',
            uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
            numbers: '0123456789',
            symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
            similar: 'il1Lo0O',
            ambiguous: '{}[]()/\\\'"`~,;.<>'
        };
    }

    /**
     * Generate a secure password
     * @param {Object} options - Password generation options
     * @returns {string} Generated password
     */
    generate(options = {}) {
        const config = { ...this.requirements, ...options };
        const length = Math.max(config.minLength, Math.min(config.maxLength, 32));

        let charset = '';
        let requiredChars = '';

        // Build character set based on requirements
        if (config.requireLowercase) {
            charset += this.characterSets.lowercase;
            requiredChars += this.getRandomChar(this.characterSets.lowercase);
        }

        if (config.requireUppercase) {
            charset += this.characterSets.uppercase;
            requiredChars += this.getRandomChar(this.characterSets.uppercase);
        }

        if (config.requireNumbers) {
            charset += this.characterSets.numbers;
            requiredChars += this.getRandomChar(this.characterSets.numbers);
        }

        if (config.requireSymbols) {
            charset += this.characterSets.symbols;
            requiredChars += this.getRandomChar(this.characterSets.symbols);
        }

        // Remove similar characters if required
        if (config.excludeSimilar) {
            charset = this.removeCharacters(charset, this.characterSets.similar);
        }

        // Remove ambiguous characters if required
        if (config.excludeAmbiguous) {
            charset = this.removeCharacters(charset, this.characterSets.ambiguous);
        }

        // Generate remaining characters
        let password = requiredChars;
        for (let i = requiredChars.length; i < length; i++) {
            password += this.getRandomChar(charset);
        }

        // Shuffle the password
        return this.shuffleString(password);
    }

    /**
     * Get a random character from a character set
     * @param {string} charset - Character set
     * @returns {string} Random character
     */
    getRandomChar(charset) {
        return charset[Math.floor(Math.random() * charset.length)];
    }

    /**
     * Remove characters from a character set
     * @param {string} charset - Character set
     * @param {string} toRemove - Characters to remove
     * @returns {string} Filtered character set
     */
    removeCharacters(charset, toRemove) {
        return charset.split('').filter(char => !toRemove.includes(char)).join('');
    }

    /**
     * Shuffle a string
     * @param {string} str - String to shuffle
     * @returns {string} Shuffled string
     */
    shuffleString(str) {
        const array = str.split('');
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array.join('');
    }

    /**
     * Validate password against requirements
     * @param {string} password - Password to validate
     * @param {Object} requirements - Requirements to check against
     * @returns {Object} Validation result
     */
    validatePassword(password, requirements = this.requirements) {
        const result = {
            isValid: true,
            errors: [],
            strength: 'weak'
        };

        if (!password) {
            result.isValid = false;
            result.errors.push('Password is required');
            return result;
        }

        if (password.length < requirements.minLength) {
            result.isValid = false;
            result.errors.push(`Password must be at least ${requirements.minLength} characters long`);
        }

        if (password.length > requirements.maxLength) {
            result.isValid = false;
            result.errors.push(`Password must be no more than ${requirements.maxLength} characters long`);
        }

        if (requirements.requireUppercase && !/[A-Z]/.test(password)) {
            result.isValid = false;
            result.errors.push('Password must contain at least one uppercase letter');
        }

        if (requirements.requireLowercase && !/[a-z]/.test(password)) {
            result.isValid = false;
            result.errors.push('Password must contain at least one lowercase letter');
        }

        if (requirements.requireNumbers && !/[0-9]/.test(password)) {
            result.isValid = false;
            result.errors.push('Password must contain at least one number');
        }

        if (requirements.requireSymbols && !/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
            result.isValid = false;
            result.errors.push('Password must contain at least one special character');
        }

        // Calculate password strength
        result.strength = this.calculateStrength(password);

        return result;
    }

    /**
     * Calculate password strength
     * @param {string} password - Password to analyze
     * @returns {string} Strength level (weak, medium, strong, very-strong)
     */
    calculateStrength(password) {
        let score = 0;

        // Length scoring
        if (password.length >= 8) score += 1;
        if (password.length >= 12) score += 1;
        if (password.length >= 16) score += 1;

        // Character variety scoring
        if (/[a-z]/.test(password)) score += 1;
        if (/[A-Z]/.test(password)) score += 1;
        if (/[0-9]/.test(password)) score += 1;
        if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score += 1;

        // Complexity scoring
        if (password.length >= 12 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
            score += 2;
        }

        if (score <= 3) return 'weak';
        if (score <= 5) return 'medium';
        if (score <= 7) return 'strong';
        return 'very-strong';
    }

    /**
     * Initialize password generator for a form
     * @param {string} formSelector - Form selector
     */
    initForm(form) {
        if (!form) {
            console.log('PasswordGenerator: No form provided');
            return;
        }

        // If form is a string selector, get the element
        if (typeof form === 'string') {
            form = document.querySelector(form);
            if (!form) {
                console.log('PasswordGenerator: Form not found with selector:', form);
                return;
            }
        }

        // Check if this is a login form - skip password generator on login forms
        const formAction = (form.action || '').toLowerCase();
        const formId = (form.id || '').toLowerCase();
        const formClass = (form.className || '').toLowerCase();

        if (formAction.includes('/login') ||
            formId.includes('login') ||
            formClass.includes('login')) {
            console.log('PasswordGenerator: Skipping login form');
            return;
        }

        const passwordInputs = form.querySelectorAll('input[type="password"]');
        console.log('PasswordGenerator: Found', passwordInputs.length, 'password inputs in form');
        passwordInputs.forEach((input, index) => {
            // Skip confirm password fields - generator doesn't make sense here
            const inputName = (input.name || '').toLowerCase();
            const inputId = (input.id || '').toLowerCase();
            if (inputName.includes('confirm') || inputName.includes('confirmation') ||
                inputId.includes('confirm') || inputId.includes('confirmation')) {
                console.log('PasswordGenerator: Skipping confirm password field', input);
                return;
            }

            console.log('PasswordGenerator: Enhancing password input', index, input);
            this.enhancePasswordInput(input);
        });
    }

    /**
     * Enhance a password input with generator and visibility toggle
     * @param {HTMLInputElement} input - Password input element
     */
    enhancePasswordInput(input) {
        // Create wrapper if it doesn't exist
        if (!input.parentElement.classList.contains('password-input-wrapper')) {
            const wrapper = document.createElement('div');
            wrapper.className = 'password-input-wrapper position-relative';
            input.parentNode.insertBefore(wrapper, input);
            wrapper.appendChild(input);
        }

        // Add password visibility toggle
        this.addVisibilityToggle(input);

        // Add password generator button
        this.addGeneratorButton(input);
    }

    /**
     * Add password visibility toggle
     * @param {HTMLInputElement} input - Password input element
     */
    addVisibilityToggle(input) {
        const wrapper = input.parentElement;

        // Check if toggle already exists
        if (wrapper.querySelector('.password-toggle')) return;

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'password-toggle btn btn-link position-absolute end-0 top-50 translate-middle-y me-3';
        toggle.innerHTML = '<i class="fas fa-eye"></i>';
        toggle.setAttribute('aria-label', 'Toggle password visibility');

        toggle.addEventListener('click', () => {
            const isPassword = input.type === 'password';
            input.type = isPassword ? 'text' : 'password';
            const icon = toggle.querySelector('i');
            icon.className = isPassword ? 'fas fa-eye-slash' : 'fas fa-eye';
            
            // Toggle active class (orange color when showing password)
            if (isPassword) {
                toggle.classList.add('active');
            } else {
                toggle.classList.remove('active');
            }
        });

        wrapper.appendChild(toggle);
    }

    /**
     * Add password generator button
     * @param {HTMLInputElement} input - Password input element
     */
    addGeneratorButton(input) {
        const wrapper = input.parentElement;

        // Check if generator already exists
        if (wrapper.querySelector('.password-generator')) return;

        const generator = document.createElement('button');
        generator.type = 'button';
        generator.className = 'password-generator btn btn-sm position-absolute end-0 top-50 translate-middle-y me-5';
        generator.innerHTML = '<i class="fas fa-dice"></i>';
        generator.setAttribute('aria-label', 'Generate secure password');
        generator.title = 'Generate secure password';
        // CSS handles color via var(--primary-color)

        generator.addEventListener('click', () => {
            const newPassword = this.generate();
            input.value = newPassword;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));

            // Add active class to keep orange after click
            generator.classList.add('active');

            // Show success message
            this.showPasswordGenerated(input);
        });

        wrapper.appendChild(generator);
    }

    /**
     * Show password generated message
     * @param {HTMLInputElement} input - Password input element
     */
    showPasswordGenerated(input) {
        // Remove existing message
        const existingMessage = input.parentElement.querySelector('.password-generated-message');
        if (existingMessage) {
            existingMessage.remove();
        }

        const message = document.createElement('div');
        message.className = 'password-generated-message text-success small';
        message.innerHTML = '<i class="fas fa-check-circle me-1"></i>Secure password generated!';

        input.parentElement.appendChild(message);

        // Remove message after 3 seconds
        setTimeout(() => {
            if (message.parentElement) {
                message.remove();
            }
        }, 3000);
    }
}

// Initialize password generator when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
    console.log('PasswordGenerator: DOM loaded, initializing...');
    window.passwordGenerator = new PasswordGenerator();

    // Initialize for all forms
    const forms = document.querySelectorAll('form');
    console.log('PasswordGenerator: Found', forms.length, 'forms');
    forms.forEach((form, index) => {
        console.log('PasswordGenerator: Initializing form', index, form);
        window.passwordGenerator.initForm(form);
    });
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = PasswordGenerator;
}


